import { all, call, put, spawn, takeLatest } from 'redux-saga/effects';
import { authPageActions as actions } from '.';
import jwtDecode from 'jwt-decode';
import api from 'app/api';
import { JwtPayload, catalogItemInfo } from './types';
import { inboxPageActions } from 'app/pages/Inbox/slice';
import { settingsActions } from 'app/pages/Settings/slice';
import { googleSheetURLToJson } from 'utils/googleSheetToJson';
import { ErrorInterface, CustomError } from 'utils/types/success-error-types';

function* signIn(action: {
  payload: { email: string; password: string; token?: string };
  type: string;
}) {
  try {
    const { email, password, token } = action.payload;

    const response = yield call(
      api.post,
      `/auth/login`,
      { email, password, token },
      { withCredentials: true },
    );

    const userToken = response.data.data.token;
    const info: JwtPayload = jwtDecode(userToken);
    info.token = userToken;

    setTokenToLocalStorage(userToken);
    if (!info?.business?.id) {
      info.isBusinessIdNotInToken = true;
      yield put(actions.signInSuccess(info));
      yield put(actions.getOrgBusinessStart());
      return;
    }
    info.isLoggedIn = true;
    info.isBusinessIdNotInToken = false;

    const bizResponse = yield getBizDetails();
    if (
      !(
        bizResponse?.data?.phoneNumberId &&
        bizResponse?.data?.businessAccountId &&
        bizResponse?.data?.fbAppId
      )
    ) {
      window.location.href = '/settings/1';
      return;
    }
    yield put(actions.signInSuccess(info));
    yield put(actions.getRolePermissionListStart());
    yield put(inboxPageActions.getTemplatesStart());
    yield put(inboxPageActions.getTemplateMediaLinkStart());
    yield put(
      inboxPageActions.updateSentBy({
        id: info.id,
        name: info.name,
        businessId: info.business.id,
      }),
    );
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.signInFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Sign in failed',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* updateToken() {
  try {
    const response = yield call(api.get, `/auth/update-token`);
    const userToken = response.data.data;
    const info: JwtPayload = jwtDecode(userToken);
    info.token = userToken;
    info.isLoggedIn = true;

    setTokenToLocalStorage(userToken);
    yield put(actions.signInSuccess(info));
    yield put(
      inboxPageActions.updateSentBy({
        id: info.id,
        name: info.name,
        businessId: info.business?.id ?? 0,
      }),
    );
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    console.error('updateToken error:', error?.response?.data);
  }
}

function* getBizDetails() {
  try {
    const response = yield call(api.get, `/business`);
    const business = response.data.data;
    yield put(actions.getBusinessDetailsSuccess(business));
    let items: catalogItemInfo[] = [];
    if (business?.catalogSheetLink && business?.catalogSheetLink?.length > 10) {
      const fetchCatalogItems = async () => {
        items = await googleSheetURLToJson(business.catalogSheetLink || '');
      };
      yield fetchCatalogItems();
      yield put(actions.setCatalogItemsSuccess(items));
    }
    return response?.data;
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.signInFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to get business details',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* getOrgBusinessDetails() {
  try {
    const response = yield call(api.get, `/org/business`);
    const businesses = response.data.data;

    // Auto-login to last selected business if token doesn't have business ID
    const lastBusinessId = localStorage.getItem('_LAST_BIZ_');
    const currentToken = localStorage.getItem('_UFT_');
    let hasBusinessInToken = false;
    if (currentToken) {
      try {
        const decoded: JwtPayload = jwtDecode(currentToken);
        hasBusinessInToken = !!decoded?.business?.id;
      } catch {
        // Invalid token, proceed with auto-login
      }
    }

    if (lastBusinessId && businesses?.length > 0 && !hasBusinessInToken) {
      const businessId = parseInt(lastBusinessId, 10);
      const businessExists = businesses.some(
        (b: { id: number }) => b.id === businessId,
      );
      if (businessExists) {
        yield put(actions.getBusinessTokenStart({ businessId }));
        return; // Don't show ChooseBusiness, auto-login will reload
      }
    }

    // Only set businesses if not auto-logging in
    yield put(actions.getOrgBusinessSuccess(businesses));
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.getOrgBusinessFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to get businesses',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* getSelectBusinessToken(action: {
  payload: { businessId: number };
  type: string;
}) {
  try {
    const { businessId } = action.payload;
    // Save last selected business for auto-login
    localStorage.setItem('_LAST_BIZ_', businessId.toString());
    const response = yield call(api.get, `/org/business-token/${businessId}`);
    const userToken = response.data.data.token;
    const info: JwtPayload = jwtDecode(userToken);
    info.token = userToken;

    setTokenToLocalStorage(userToken);
    if (!info?.business?.id) {
      info.isBusinessIdNotInToken = true;
      yield put(actions.signInSuccess(info));
      yield put(actions.getOrgBusinessStart());
      return;
    }
    info.isLoggedIn = true;
    info.isBusinessIdNotInToken = false;

    const bizResponse = yield getBizDetails();
    if (
      !(
        bizResponse?.data?.phoneNumberId &&
        bizResponse?.data?.businessAccountId &&
        bizResponse?.data?.fbAppId
      )
    ) {
      window.location.href = '/settings/1';
      return;
    }
    yield put(actions.signInSuccess(info));
    yield put(actions.getRolePermissionListStart());
    yield put(inboxPageActions.getTemplatesStart());
    yield put(inboxPageActions.getTemplateMediaLinkStart());
    yield put(
      inboxPageActions.updateSentBy({
        id: info.id,
        name: info.name,
        businessId: info.business.id,
      }),
    );
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.signInFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to get business token',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  } finally {
    sessionStorage.removeItem('selectedClientWaNumber');
    window.location.reload();
  }
}

function* addNewBusiness() {
  try {
    const response = yield call(api.post, `/org/business`, {});
    const { businesses, newBusinessId } = response.data.data;

    // Update the businesses list
    yield put(actions.getOrgBusinessSuccess(businesses));

    // Auto-select the newly created business
    if (newBusinessId) {
      yield put(actions.getBusinessTokenStart({ businessId: newBusinessId }));
    }
  } catch (err) {
    const error = err as ErrorInterface;
    yield put(
      actions.getOrgBusinessFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to create business',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* updateBusinessName(action: {
  payload: { businessId: number; name: string };
  type: string;
}) {
  try {
    const { businessId, name } = action.payload;
    const response = yield call(api.put, `/org/business/${businessId}`, {
      name,
    });
    yield put(actions.updateBusinessNameSuccess(response.data.data));
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.updateBusinessNameFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to update business name',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* deleteBusiness(action: {
  payload: { businessId: number };
  type: string;
}) {
  try {
    const { businessId } = action.payload;
    const response = yield call(api.delete, `/org/business/${businessId}`);
    yield put(actions.deleteBusinessSuccess(response.data.data));
  } catch (err) {
    const error = err as ErrorInterface;
    yield put(
      actions.deleteBusinessFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to delete business',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

// 2FA Saga Functions
function* enable2FA() {
  try {
    const response = yield call(api.get, `/auth/2fa/enable`);
    yield put(actions.enable2FASuccess(response.data.data));
    yield put(settingsActions.fetchBizEmployeesStart());
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.enable2FAFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to enable 2FA',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* verify2FA(action: { payload: { token: string }; type: string }) {
  try {
    const { token } = action.payload;
    yield call(api.post, `/auth/2fa/verify`, { token });
    yield put(actions.verify2FASuccess());
    yield put(settingsActions.fetchBizEmployeesStart());
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.verify2FAFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to verify 2FA',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* disable2FA(action: { payload: { token: string }; type: string }) {
  try {
    const { token } = action.payload;
    yield call(api.post, `/auth/2fa/disable`, { token });
    yield put(actions.disable2FASuccess());
    yield put(settingsActions.fetchBizEmployeesStart());
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.disable2FAFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to disable 2FA',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

function* getRolePermissionList() {
  try {
    const response = yield call(api.get, `/role-permission`);
    yield put(actions.getRolePermissionListSuccess(response.data.data));
  } catch (err: unknown) {
    const error = err as ErrorInterface;
    yield put(
      actions.signInFailure(
        (error?.response?.data as CustomError) ?? {
          errorType: 'Error',
          errorMessage: 'Failed to get role permissions',
          errorRaw: null,
          errorsValidation: null,
        },
      ),
    );
  }
}

export function setTokenToLocalStorage(token: string) {
  localStorage.setItem('_UFT_', `${token}`);
}

function* watchSignIn() {
  yield takeLatest(actions.signInStart.type, signIn);
}

function* watchUpdateJwtToken() {
  yield takeLatest(actions.updateJwtTokenStart.type, updateToken);
}

function* watchGetBusinessDetails() {
  yield takeLatest(actions.getBusinessDetails.type, getBizDetails);
}

function* watchGetRolePermissionList() {
  yield takeLatest(
    actions.getRolePermissionListStart.type,
    getRolePermissionList,
  );
}

function* watchGetOrgBusinessDetails() {
  yield takeLatest(actions.getOrgBusinessStart.type, getOrgBusinessDetails);
}

function* watchGetSelectBusinessToken() {
  yield takeLatest(actions.getBusinessTokenStart.type, getSelectBusinessToken);
}

function* watchAddNewBusiness() {
  yield takeLatest(actions.addNewBusinessStart.type, addNewBusiness);
}

function* watchUpdateBusinessName() {
  yield takeLatest(actions.updateBusinessNameStart.type, updateBusinessName);
}

function* watchDeleteBusiness() {
  yield takeLatest(actions.deleteBusinessStart.type, deleteBusiness);
}

// 2FA Watchers
function* watchEnable2FA() {
  yield takeLatest(actions.enable2FAStart.type, enable2FA);
}

function* watchVerify2FA() {
  yield takeLatest(actions.verify2FAStart.type, verify2FA);
}

function* watchDisable2FA() {
  yield takeLatest(actions.disable2FAStart.type, disable2FA);
}

export function* authPageSaga() {
  const sagas = [
    watchSignIn,
    watchUpdateJwtToken,
    watchGetBusinessDetails,
    watchGetRolePermissionList,
    watchGetOrgBusinessDetails,
    watchGetSelectBusinessToken,
    watchAddNewBusiness,
    watchUpdateBusinessName,
    watchDeleteBusiness,
    watchEnable2FA,
    watchVerify2FA,
    watchDisable2FA,
  ];

  yield all(
    sagas.map(saga =>
      spawn(function* () {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            console.error('Saga error in authPageSaga:', e);
          }
        }
      }),
    ),
  );
}
